import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';

type Step = 1 | 2 | 3 | 4;

interface FormData {
  // Step 1 — Property Type
  propertyType: string;
  propertySize: string;
  // Step 2 — Security Needs
  services: string[];
  currentSecurity: string;
  // Step 3 — Location & Timing
  city: string;
  timeline: string;
  // Step 4 — Contact Info
  fullName: string;
  phone: string;
  email: string;
  details: string;
}

const INITIAL_FORM: FormData = {
  propertyType: '',
  propertySize: '',
  services: [],
  currentSecurity: '',
  city: '',
  timeline: '',
  fullName: '',
  phone: '',
  email: '',
  details: '',
};

const STEPS = [
  { number: 1, label: 'Property' },
  { number: 2, label: 'Services' },
  { number: 3, label: 'Location' },
  { number: 4, label: 'Contact' },
];

const PROPERTY_TYPES = [
  { value: 'detached-home', label: 'Detached Home', icon: 'ri-home-4-line' },
  { value: 'condo-apartment', label: 'Condo / Apartment', icon: 'ri-building-line' },
  { value: 'townhouse', label: 'Townhouse', icon: 'ri-community-line' },
  { value: 'small-business', label: 'Small Business', icon: 'ri-store-2-line' },
  { value: 'office', label: 'Office / Commercial', icon: 'ri-building-4-line' },
  { value: 'warehouse', label: 'Warehouse / Industrial', icon: 'ri-warehouse-line' },
];

const PROPERTY_SIZES = [
  { value: 'under-1000', label: 'Under 1,000 sq ft' },
  { value: '1000-2500', label: '1,000 – 2,500 sq ft' },
  { value: '2500-5000', label: '2,500 – 5,000 sq ft' },
  { value: 'over-5000', label: 'Over 5,000 sq ft' },
];

const SERVICES = [
  { value: 'alarm-system', label: 'Alarm System', icon: 'ri-alarm-warning-line' },
  { value: 'security-cameras', label: 'Security Cameras', icon: 'ri-camera-line' },
  { value: 'access-control', label: 'Access Control', icon: 'ri-door-lock-line' },
  { value: 'live-monitoring', label: '24/7 Live Monitoring', icon: 'ri-eye-line' },
  { value: 'smart-home', label: 'Smart Home Integration', icon: 'ri-home-gear-line' },
  { value: 'video-doorbell', label: 'Video Doorbell', icon: 'ri-door-open-line' },
];

const CURRENT_SECURITY = [
  { value: 'none', label: 'No current system' },
  { value: 'basic', label: 'Basic alarm only' },
  { value: 'cameras', label: 'Cameras only' },
  { value: 'full', label: 'Full system — upgrading' },
];

const CITIES = [
  'Toronto', 'Mississauga', 'Brampton', 'Oakville',
  'Hamilton', 'Richmond Hill', 'Vaughan', 'Whitby', 'Ajax', 'Oshawa', 'Other',
];

const TIMELINES = [
  { value: 'asap', label: 'As soon as possible', icon: 'ri-flashlight-line' },
  { value: '1-month', label: 'Within 1 month', icon: 'ri-calendar-check-line' },
  { value: '3-months', label: 'Within 3 months', icon: 'ri-calendar-line' },
  { value: 'just-browsing', label: 'Just exploring options', icon: 'ri-search-eye-line' },
];

// ============================================================
// 🔗 CRM WEBHOOK INTEGRATION
// Paste your Zapier / Make webhook URL below.
// Leave empty ("") to disable.
// ============================================================
const ZAPIER_WEBHOOK_URL = '';

export default function FreeQuotePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [detailsLength, setDetailsLength] = useState(0);

  const toggleService = (value: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(value)
        ? prev.services.filter(s => s !== value)
        : [...prev.services, value],
    }));
  };

  const canProceed = () => {
    if (step === 1) return formData.propertyType !== '' && formData.propertySize !== '';
    if (step === 2) return formData.services.length > 0 && formData.currentSecurity !== '';
    if (step === 3) return formData.city !== '' && formData.timeline !== '';
    if (step === 4) return formData.fullName.trim() !== '' && formData.phone.trim() !== '' && formData.email.trim() !== '';
    return false;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canProceed()) return;
    setFormStatus('submitting');

    if (formData.details.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('fullName', formData.fullName);
      params.append('email', formData.email);
      params.append('phone', formData.phone);
      params.append('propertyType', formData.propertyType);
      params.append('propertySize', formData.propertySize);
      params.append('services', formData.services.join(', '));
      params.append('currentSecurity', formData.currentSecurity);
      params.append('city', formData.city);
      params.append('timeline', formData.timeline);
      params.append('details', formData.details);

      const response = await fetch('https://readdy.ai/api/form/d6hp7354ieqv1pmm5cag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (response.ok) {
        // Fire webhook to CRM (Zapier / Make / HubSpot etc.)
        if (ZAPIER_WEBHOOK_URL) {
          try {
            await fetch(ZAPIER_WEBHOOK_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                propertyType: formData.propertyType,
                propertySize: formData.propertySize,
                services: formData.services.join(', '),
                currentSecurity: formData.currentSecurity,
                city: formData.city,
                timeline: formData.timeline,
                details: formData.details,
                source: 'Free Quote Page',
                submitted_at: new Date().toISOString(),
              }),
            });
          } catch (webhookErr) {
            console.warn('Webhook delivery failed (non-blocking):', webhookErr);
          }
        }
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const progressPercent = ((step - 1) / 3) * 100;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Get a Free Security Quote | Alliance Security Systems GTA"
        description="Get a free, no-obligation security quote in minutes. Tell us about your property and security needs — our experts will recommend the perfect alarm, camera, or access control solution for your home or business in the GTA."
        keywords="free security quote, home security quote, alarm system quote, security camera quote, GTA security systems"
        canonical="/free-quote"
      />

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex flex-col leading-none">
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Home</Link>
            <Link to="/alarm-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Alarms</Link>
            <Link to="/camera-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Cameras</Link>
            <Link to="/access-control" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Access Control</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line"></i>1-888-458-9181
            </a>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            <Link to="/" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Home</Link>
            <Link to="/alarm-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Alarms</Link>
            <Link to="/camera-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Cameras</Link>
            <Link to="/access-control" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Access Control</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853]"><i className="ri-phone-line"></i>1-888-458-9181</a>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Banner */}
        <section className="relative bg-[#0A1628] py-12 sm:py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://public.readdy.ai/ai/img_res/bb072e52aa5cdf0aef0df7068d9c2c09.jpg"
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 to-[#0A1628]/70"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-white/50 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white/80 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">Free Quote</span>
            </nav>
            <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">
              NO OBLIGATION · 100% FREE
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-4 leading-tight">
              Get Your Free Security Quote
            </h1>
            <p className="text-base sm:text-lg text-white/70 max-w-xl mx-auto mb-8">
              Answer 4 quick questions and our security experts will build a custom solution for your property — at no cost.
            </p>
            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8">
              {[
                { icon: 'ri-time-line', text: '2-Hour Response' },
                { icon: 'ri-shield-check-line', text: 'Licensed Technicians' },
                { icon: 'ri-price-tag-3-line', text: 'No Hidden Fees' },
                { icon: 'ri-star-line', text: '4.9★ Rated' },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2 text-white/70 text-xs sm:text-sm">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${b.icon} text-[#D4A853]`}></i>
                  </div>
                  <span>{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Multi-Step Form */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-[#F8F9FA] to-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">

            {formStatus === 'success' ? (
              <SuccessScreen onReset={() => { setFormStatus('idle'); setStep(1); setFormData(INITIAL_FORM); }} navigate={navigate} />
            ) : (
              <form
                id="free-quote-multistep"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
              >
                {/* Step Progress Header */}
                <div className="px-6 sm:px-10 pt-8 pb-6 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    {STEPS.map((s, idx) => (
                      <div key={s.number} className="flex items-center flex-1">
                        <div className="flex flex-col items-center">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                            step > s.number
                              ? 'bg-emerald-500 text-white'
                              : step === s.number
                              ? 'bg-[#D4A853] text-white shadow-lg shadow-[#D4A853]/30'
                              : 'bg-gray-100 text-gray-400'
                          }`}>
                            {step > s.number ? <i className="ri-check-line text-base"></i> : s.number}
                          </div>
                          <span className={`text-xs mt-1.5 font-medium whitespace-nowrap ${step === s.number ? 'text-[#D4A853]' : step > s.number ? 'text-emerald-500' : 'text-gray-400'}`}>
                            {s.label}
                          </span>
                        </div>
                        {idx < STEPS.length - 1 && (
                          <div className="flex-1 h-0.5 mx-2 mb-5 rounded-full overflow-hidden bg-gray-100">
                            <div
                              className="h-full bg-emerald-500 transition-all duration-500"
                              style={{ width: step > s.number ? '100%' : '0%' }}
                            ></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Overall progress bar */}
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#D4A853] rounded-full transition-all duration-500"
                      style={{ width: `${progressPercent + (step === 4 ? 33 : 0)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-right">Step {step} of 4</p>
                </div>

                <div className="px-6 sm:px-10 py-8">
                  {/* STEP 1 — Property Type */}
                  {step === 1 && (
                    <div className="animate-fade-in">
                      <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mb-2">What type of property?</h2>
                      <p className="text-gray-500 text-sm mb-8">Select the option that best describes your property.</p>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
                        {PROPERTY_TYPES.map(pt => (
                          <button
                            key={pt.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, propertyType: pt.value }))}
                            className={`flex flex-col items-center gap-3 p-4 sm:p-5 rounded-2xl border-2 transition-all cursor-pointer text-center ${
                              formData.propertyType === pt.value
                                ? 'border-[#D4A853] bg-[#D4A853]/5 shadow-md'
                                : 'border-gray-200 hover:border-[#D4A853]/50 hover:bg-gray-50'
                            }`}
                          >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${formData.propertyType === pt.value ? 'bg-[#D4A853]' : 'bg-gray-100'}`}>
                              <i className={`${pt.icon} text-2xl ${formData.propertyType === pt.value ? 'text-white' : 'text-gray-500'}`}></i>
                            </div>
                            <span className={`text-xs sm:text-sm font-semibold leading-tight ${formData.propertyType === pt.value ? 'text-[#D4A853]' : 'text-gray-700'}`}>{pt.label}</span>
                          </button>
                        ))}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-3">Property Size</label>
                        <div className="grid grid-cols-2 gap-3">
                          {PROPERTY_SIZES.map(ps => (
                            <button
                              key={ps.value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, propertySize: ps.value }))}
                              className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer text-left ${
                                formData.propertySize === ps.value
                                  ? 'border-[#D4A853] bg-[#D4A853]/5 text-[#D4A853]'
                                  : 'border-gray-200 text-gray-600 hover:border-[#D4A853]/50'
                              }`}
                            >
                              {ps.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 2 — Security Needs */}
                  {step === 2 && (
                    <div className="animate-fade-in">
                      <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mb-2">What security do you need?</h2>
                      <p className="text-gray-500 text-sm mb-8">Select all services you're interested in.</p>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
                        {SERVICES.map(svc => (
                          <button
                            key={svc.value}
                            type="button"
                            onClick={() => toggleService(svc.value)}
                            className={`flex flex-col items-center gap-3 p-4 sm:p-5 rounded-2xl border-2 transition-all cursor-pointer text-center relative ${
                              formData.services.includes(svc.value)
                                ? 'border-[#D4A853] bg-[#D4A853]/5 shadow-md'
                                : 'border-gray-200 hover:border-[#D4A853]/50 hover:bg-gray-50'
                            }`}
                          >
                            {formData.services.includes(svc.value) && (
                              <div className="absolute top-2 right-2 w-5 h-5 bg-[#D4A853] rounded-full flex items-center justify-center">
                                <i className="ri-check-line text-white text-xs"></i>
                              </div>
                            )}
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${formData.services.includes(svc.value) ? 'bg-[#D4A853]' : 'bg-gray-100'}`}>
                              <i className={`${svc.icon} text-2xl ${formData.services.includes(svc.value) ? 'text-white' : 'text-gray-500'}`}></i>
                            </div>
                            <span className={`text-xs sm:text-sm font-semibold leading-tight ${formData.services.includes(svc.value) ? 'text-[#D4A853]' : 'text-gray-700'}`}>{svc.label}</span>
                          </button>
                        ))}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-3">Current Security Setup</label>
                        <div className="grid grid-cols-2 gap-3">
                          {CURRENT_SECURITY.map(cs => (
                            <button
                              key={cs.value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, currentSecurity: cs.value }))}
                              className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer text-left ${
                                formData.currentSecurity === cs.value
                                  ? 'border-[#D4A853] bg-[#D4A853]/5 text-[#D4A853]'
                                  : 'border-gray-200 text-gray-600 hover:border-[#D4A853]/50'
                              }`}
                            >
                              {cs.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 3 — Location & Timing */}
                  {step === 3 && (
                    <div className="animate-fade-in">
                      <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mb-2">Where are you located?</h2>
                      <p className="text-gray-500 text-sm mb-8">We serve the entire Greater Toronto Area.</p>

                      <div className="mb-8">
                        <label className="block text-sm font-semibold text-[#0A1628] mb-3">Your City</label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {CITIES.map(city => (
                            <button
                              key={city}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, city }))}
                              className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer text-center ${
                                formData.city === city
                                  ? 'border-[#D4A853] bg-[#D4A853]/5 text-[#D4A853]'
                                  : 'border-gray-200 text-gray-600 hover:border-[#D4A853]/50'
                              }`}
                            >
                              {city}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-3">When do you need this?</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {TIMELINES.map(tl => (
                            <button
                              key={tl.value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, timeline: tl.value }))}
                              className={`flex items-center gap-3 py-3.5 px-5 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer ${
                                formData.timeline === tl.value
                                  ? 'border-[#D4A853] bg-[#D4A853]/5 text-[#D4A853]'
                                  : 'border-gray-200 text-gray-600 hover:border-[#D4A853]/50'
                              }`}
                            >
                              <div className="w-6 h-6 flex items-center justify-center shrink-0">
                                <i className={`${tl.icon} text-lg`}></i>
                              </div>
                              {tl.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 4 — Contact Info */}
                  {step === 4 && (
                    <div className="animate-fade-in">
                      <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mb-2">Almost done!</h2>
                      <p className="text-gray-500 text-sm mb-8">Where should we send your free quote?</p>

                      {/* Summary Card */}
                      <div className="bg-[#F8F9FA] rounded-2xl p-5 mb-8 border border-gray-100">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Your Quote Summary</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-400 text-xs">Property</span>
                            <p className="font-semibold text-[#0A1628] capitalize">{formData.propertyType.replace(/-/g, ' ')}</p>
                          </div>
                          <div>
                            <span className="text-gray-400 text-xs">Size</span>
                            <p className="font-semibold text-[#0A1628]">{PROPERTY_SIZES.find(p => p.value === formData.propertySize)?.label}</p>
                          </div>
                          <div>
                            <span className="text-gray-400 text-xs">Services</span>
                            <p className="font-semibold text-[#0A1628]">{formData.services.length} selected</p>
                          </div>
                          <div>
                            <span className="text-gray-400 text-xs">City</span>
                            <p className="font-semibold text-[#0A1628]">{formData.city}</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-sm font-semibold text-[#0A1628] mb-2">Full Name *</label>
                            <input
                              type="text"
                              name="fullName"
                              required
                              value={formData.fullName}
                              onChange={e => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                              placeholder="John Smith"
                              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0A1628] mb-2">Phone Number *</label>
                            <input
                              type="tel"
                              name="phone"
                              required
                              value={formData.phone}
                              onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                              placeholder="(416) 555-0123"
                              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="john@example.com"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Anything else we should know? <span className="text-gray-400 font-normal">(optional)</span></label>
                          <textarea
                            name="details"
                            rows={3}
                            maxLength={500}
                            value={formData.details}
                            onChange={e => { setFormData(prev => ({ ...prev, details: e.target.value })); setDetailsLength(e.target.value.length); }}
                            placeholder="e.g. I have a dog, specific entry points, previous break-in..."
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all resize-none"
                          ></textarea>
                          <p className="text-xs text-gray-400 mt-1 text-right">{detailsLength}/500</p>
                        </div>
                      </div>

                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl text-sm mt-4">
                          <i className="ri-error-warning-line text-lg"></i>
                          Something went wrong. Please try again or call us at 1-888-458-9181.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-100">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={() => setStep(prev => (prev - 1) as Step)}
                        className="flex items-center gap-2 text-gray-500 hover:text-[#0A1628] font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-line text-lg"></i>
                        Back
                      </button>
                    ) : (
                      <div></div>
                    )}

                    {step < 4 ? (
                      <button
                        type="button"
                        disabled={!canProceed()}
                        onClick={() => setStep(prev => (prev + 1) as Step)}
                        className="flex items-center gap-2 bg-[#D4A853] text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-[#D4A853]/20"
                      >
                        Continue
                        <i className="ri-arrow-right-line text-lg"></i>
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={!canProceed() || formStatus === 'submitting'}
                        className="flex items-center gap-2 bg-[#D4A853] text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-[#D4A853]/20"
                      >
                        {formStatus === 'submitting' ? (
                          <><i className="ri-loader-4-line animate-spin text-lg"></i>Sending...</>
                        ) : (
                          <><i className="ri-send-plane-line text-lg"></i>Get My Free Quote</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            )}

            {/* Side trust panel below form on mobile / beside on desktop */}
            {formStatus !== 'success' && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-lock-2-line', title: 'Private & Secure', desc: 'Your info is never shared with third parties.' },
                  { icon: 'ri-customer-service-2-line', title: 'Expert Advice', desc: 'A certified specialist reviews every quote.' },
                  { icon: 'ri-money-dollar-circle-line', title: 'Zero Cost', desc: 'The quote and consultation are completely free.' },
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                    <div className="w-9 h-9 bg-[#D4A853]/10 rounded-xl flex items-center justify-center shrink-0">
                      <i className={`${t.icon} text-lg text-[#D4A853]`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#0A1628]">{t.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Social Proof Strip */}
        <section className="py-10 bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <p className="text-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">What our customers say</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { name: 'Michael T.', city: 'Toronto', stars: 5, text: 'The quote process was incredibly fast. Within 2 hours I had a detailed proposal and the installation was done the next week.' },
                { name: 'Sarah K.', city: 'Mississauga', stars: 5, text: 'I was nervous about the cost but the free quote showed me exactly what I needed — no upselling, just honest advice.' },
                { name: 'David R.', city: 'Brampton', stars: 5, text: 'Got quotes from 3 companies. Alliance was the most transparent and professional. Highly recommend starting with their free quote.' },
              ].map((r, i) => (
                <div key={i} className="bg-[#F8F9FA] rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: r.stars }).map((_, si) => (
                      <div key={si} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-[#D4A853] text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">"{r.text}"</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#D4A853]/20 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-[#D4A853] text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#0A1628]">{r.name}</p>
                      <p className="text-xs text-gray-400">{r.city}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-12 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-white/60 text-sm mb-3">Prefer to speak with someone directly?</p>
            <a href="tel:18884589181" className="inline-flex items-center gap-3 text-white font-bold text-2xl hover:text-[#D4A853] transition-colors cursor-pointer">
              <div className="w-10 h-10 bg-[#D4A853] rounded-full flex items-center justify-center">
                <i className="ri-phone-fill text-white text-lg"></i>
              </div>
              1-888-458-9181
            </a>
            <p className="text-white/40 text-xs mt-3">Available Mon–Fri 8am–8pm · Sat 9am–5pm</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[9px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <div className="flex items-center gap-6 text-xs text-white/50">
              <Link to="/privacy-policy" className="hover:text-white/80 cursor-pointer">Privacy Policy</Link>
              <Link to="/contact" className="hover:text-white/80 cursor-pointer">Contact</Link>
              <Link to="/faq" className="hover:text-white/80 cursor-pointer">FAQ</Link>
            </div>
            <p className="text-white/40 text-xs">© 2024 Alliance Security Systems. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SuccessScreen({ onReset, navigate }: { onReset: () => void; navigate: ReturnType<typeof useNavigate> }) {
  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      <div className="px-6 sm:px-10 py-16 text-center">
        <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <i className="ri-check-double-line text-5xl text-emerald-500"></i>
        </div>
        <h2 className="text-3xl font-bold text-[#0A1628] mb-3">Quote Request Sent!</h2>
        <p className="text-gray-500 text-base max-w-md mx-auto mb-8 leading-relaxed">
          Thank you! One of our security specialists will review your details and contact you within <strong>2 business hours</strong> with a personalized quote.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 bg-[#0A1628] text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#162440] transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            Back to Home
          </button>
          <button
            onClick={onReset}
            className="flex items-center gap-2 border-2 border-gray-200 text-gray-600 px-8 py-3.5 rounded-full font-semibold text-sm hover:border-[#D4A853] hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Submit Another
          </button>
        </div>
        <div className="mt-10 pt-8 border-t border-gray-100">
          <p className="text-sm text-gray-500 mb-4">While you wait, explore our services:</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/alarm-packages" className="text-sm text-[#D4A853] font-semibold hover:underline cursor-pointer whitespace-nowrap">Alarm Packages</Link>
            <span className="text-gray-300">·</span>
            <Link to="/camera-packages" className="text-sm text-[#D4A853] font-semibold hover:underline cursor-pointer whitespace-nowrap">Camera Packages</Link>
            <span className="text-gray-300">·</span>
            <Link to="/access-control" className="text-sm text-[#D4A853] font-semibold hover:underline cursor-pointer whitespace-nowrap">Access Control</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
